import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidEmail } from 'utils/isValidEmail';
import { v4 as uuidv4 } from 'uuid';
import CloseX from '../../icon/icons/IcoCloseX';
import { useIsAuthenticated } from 'hooks/useIsAuthenticated';
import { useViewerInfo } from './useViewerInfo';
import { useGetDocumentPreviewEmailVisibility } from 'hooks/useGetDocumentPreviewEmailVisibility';
import type { DocumentStatus } from 'services/documents/entities';
import { useMarkDocumentAsViewed } from 'hooks/useMarkDocumentAsViewed';
import { GUEST_EMAIL } from 'providers/LiveblocksProvider';

interface IdentifyToViewModalProps {
  senderName: string;
  documentId: string;
  documentStatus: DocumentStatus;
  contentId: string;
  accountId: string;
  createdBy: string;
  assigneeUserId?: string;
  documentTitle: string;
  userData?: { email?: string; id?: string };
}

export const IdentifyToViewModal = ({
  senderName,
  documentId,
  documentStatus,
  contentId,
  accountId,
  createdBy,
  assigneeUserId,
  documentTitle,
  userData,
}: IdentifyToViewModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState(false);
  const [inputEmailError, setInputEmailError] = useState(false);
  const { viewer, setViewerEmail, clearViewerEmail, setViewerDetails } = useViewerInfo();
  const { mutateAsync: markDocumentAsViewed } = useMarkDocumentAsViewed({});
  const markedViewedKeyRef = useRef<string | null>(null);
  const authenticationStatus = useIsAuthenticated();
  const isAnonymousViewer = authenticationStatus === 'anonymous';

  const userEmail = userData?.email;
  const userId = userData?.id;

  const { previewEmailVisibility, isLoading: isLoadingPreviewEmailVisibility } = useGetDocumentPreviewEmailVisibility({
    documentId,
    contentId,
  });

  useEffect(() => {
    const isEmailVisibilityLoaded = !isLoadingPreviewEmailVisibility;
    const isEmailVisibilityDisabled = !previewEmailVisibility?.has_email_visibility;
    const isDefaultCommentsStatus = viewer.commentsStatus === 'default';
    const shouldSetUnidentifiedViewer =
      isEmailVisibilityLoaded && isEmailVisibilityDisabled && isDefaultCommentsStatus && isAnonymousViewer;
    // A known logged-in viewer's identity must win over the anonymous-UUID assignment below -
    // otherwise it silently overwrites a real user's email with a random anonymous id.
    if (userEmail && userId) {
      setViewerDetails(String(userData?.id), userData?.email as string);
    } else if (shouldSetUnidentifiedViewer && !viewer.id) {
      // Only assign once - without this guard the effect re-fires on every
      // dependency change and mints a new random viewer.id each time.
      // Reads localStorage directly since the closure's viewer.id can lag
      // a just-completed setViewerDetails call during rapid re-renders.
      let alreadyHasId = false;
      try {
        const raw = window.localStorage.getItem('viewer');
        alreadyHasId = !!(raw && JSON.parse(raw)?.id);
      } catch {
        alreadyHasId = false;
      }
      if (!alreadyHasId) {
        setViewerDetails(uuidv4(), null);
      }
    }
  }, [previewEmailVisibility, isLoadingPreviewEmailVisibility, viewer.commentsStatus, viewer.id, userId, userEmail, isAnonymousViewer]);

  useEffect(() => {
    if ((previewEmailVisibility?.has_email_visibility && !viewer.email) || !viewer.id) {
      setModalOpen(true);
    }
  }, [viewer]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputEmail = event.target.value;
    setViewerEmail(inputEmail);
  };

  const handleEmailBlur = () => {
    setInputEmailError(viewer.email ? viewer.email.length > 0 && !isValidEmail(viewer.email) : false);
  };

  const handleViewDocumentButtonClick = () => {
    const isValid = viewer.email ? isValidEmail(viewer.email) : false;
    setInputEmailError(!isValid);

    if (isValid) {
      setViewerDetails(uuidv4(), viewer.email as string, 'completed');
      setModalOpen(false);
    }
  };

  const handleClearInputButtonClick = () => {
    clearViewerEmail();
    setInputEmailError(false);
  };

  const isUserNotAuthenticated = authenticationStatus !== 'authenticated';
  const hasEmailVisibility = previewEmailVisibility?.has_email_visibility;
  const isPendingCommentsStatus = viewer.commentsStatus === 'pending';
  const shouldShowIdentifyToViewModal = isUserNotAuthenticated && (hasEmailVisibility || isPendingCommentsStatus);

  const isCommentIdentifyFlow = !previewEmailVisibility?.has_email_visibility && viewer.commentsStatus === 'pending';

  const handleCancelIdentify = () => {
    if (isCommentIdentifyFlow) {
      setViewerDetails(viewer.id, null);
      setModalOpen(false);
    }
  };

  const hasViewerId = !!viewer.id;

  useEffect(() => {
    if (isLoadingPreviewEmailVisibility) {
      return;
    }

    const hasViewerEmail = !!viewer.email;
    const isModalClosed = !modalOpen;
    const isNotGuestEmail = viewer.email !== GUEST_EMAIL;
    const isUserEmailProvided = hasEmailVisibility && hasViewerEmail && isModalClosed && isNotGuestEmail;
    const isCommentIdentifyPending = viewer.commentsStatus === 'pending';
    const shouldUpdateStatus =
      isAnonymousViewer && hasViewerId && !isCommentIdentifyPending && (!hasEmailVisibility || isUserEmailProvided);

    // Dedupe on viewer identity, not just "already fired" - a query refetch
    // changing previewEmailVisibility's object reference shouldn't re-fire this,
    // but a real identity change (e.g. anonymous -> identified) should.
    const viewedKey = `${viewer.id}:${viewer.email}`;
    if (shouldUpdateStatus && markedViewedKeyRef.current !== viewedKey) {
      markedViewedKeyRef.current = viewedKey;
      markDocumentAsViewed({
        document: {
          id: documentId,
          contentId,
          accountId,
          createdBy,
          assigneeUserId: String(assigneeUserId),
          title: documentTitle,
        },
        viewer: {
          id: viewer.id,
          email: viewer.email,
        },
      });
    }
  }, [
    documentStatus,
    previewEmailVisibility,
    isLoadingPreviewEmailVisibility,
    hasViewerId,
    viewer.email,
    viewer.commentsStatus,
    modalOpen,
    isAnonymousViewer,
  ]);

  if (!shouldShowIdentifyToViewModal) return null;

  return (
    <Dialog
      data-testid="identify-to-view-modal"
      open={modalOpen || viewer.commentsStatus === 'pending'}
      fullWidth
      onClose={isCommentIdentifyFlow ? handleCancelIdentify : undefined}
    >
      <DialogTitle>
        <Typography variant={'h6'} color={theme.palette.text.primary}>
          {senderName
            ? t('document.preview.identify_to_view.title_with_sender_name', { senderName })
            : t('document.preview.identify_to_view.title_without_sender_name')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ paddingTop: theme.spacing(1) }}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel htmlFor="email">{t('document.preview.identify_to_view.email_placeholder')}</InputLabel>
            <OutlinedInput
              data-testid="identify-to-view-modal-textfield"
              id="email"
              name="email"
              type="email"
              label={t('document.preview.identify_to_view.email_placeholder')}
              value={viewer.email}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              error={inputEmailError}
              inputProps={{
                'aria-label': t('document.preview.identify_to_view.email_placeholder'),
              }}
              fullWidth
              sx={{
                borderRadius: theme.spacing(1),
              }}
              endAdornment={
                viewer.email && (
                  <InputAdornment position="end">
                    <IconButton data-testid="identify-to-view-modal-clear-input" onClick={handleClearInputButtonClick}>
                      <CloseX />
                    </IconButton>
                  </InputAdornment>
                )
              }
            />
            <Typography color="error" variant="caption" style={{ height: theme.spacing(2) }}>
              {inputEmailError ? t('document.preview.identify_to_view.error_message') : ''}
            </Typography>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: 2, paddingBottom: 3 }}>
        {isCommentIdentifyFlow && <Button onClick={handleCancelIdentify}>{t('comments.cancel')}</Button>}
        <Button
          data-testid="identify-to-view-modal-button"
          variant="contained"
          disabled={inputEmailError || !viewer.email || viewer.email.length === 0}
          onClick={handleViewDocumentButtonClick}
        >
          {t('document.preview.identify_to_view.button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
